import { useState } from "react";
import TextInput from "../../../components/common/TextInput";
import type { AnalyticsPageJourneyFilterRestriction } from "../../../types/analytics/AnalyticsFilterInputs";
import { CreateAnalyticsPageFormData } from "../../../types/analytics/admin/CreateAnalyticsPageFormData";
import { AnalyticsNavItem } from "../../../types/dtos/users/SignedInUserDto";
import { useTranslation } from "react-i18next";

interface CreateAnalyticsPageProps {
  onCancel: () => void;
  onSubmit: (data: CreateAnalyticsPageFormData) => void;
  analyticsPages: AnalyticsNavItem[];
  isSubmitting?: boolean;
}

const CreateAnalyticsPage = ({
  onCancel,
  onSubmit,
  /** The current dynamic analytics pages to show as options for placement of the new page */
  analyticsPages,
  isSubmitting = false,
}: CreateAnalyticsPageProps) => {
  const { t } = useTranslation();
  const [shortTitle, setShortTitle] = useState("");
  const [longTitle, setLongTitle] = useState("");
  const [sequenceBeforePageId, setSequenceBeforePageId] = useState<
    number | null
  >(null);
  const [journeyFilterRestriction, setJourneyFilterRestriction] =
    useState<AnalyticsPageJourneyFilterRestriction>("SHOW-ALL");
  const [defaultJourneyFilterPattern, setDefaultJourneyFilterPattern] =
    useState("");
  const [touched, setTouched] = useState({
    shortTitle: false,
    longTitle: false,
    defaultJourneyFilterPattern: false,
  });

  const isShortTitleValid =
    shortTitle.trim().length > 0 && shortTitle.length <= 50;
  const isLongTitleValid =
    longTitle.trim().length > 0 && longTitle.length <= 100;
  const isDefaultJourneyFilterPatternValid =
    defaultJourneyFilterPattern.length <= 100;
  const isFormValid =
    isShortTitleValid && isLongTitleValid && isDefaultJourneyFilterPatternValid;

  return (
    <form
      className="flex flex-col gap-6"
      onSubmit={(e) => {
        e.preventDefault();
        if (isFormValid) {
          onSubmit({
            shortTitle,
            longTitle,
            sequenceValueBeforePageId: sequenceBeforePageId,
            journeyFilterRestriction,
            defaultJourneyFilterPattern,
          });
        } else {
          setTouched({
            shortTitle: true,
            longTitle: true,
            defaultJourneyFilterPattern: true,
          });
        }
      }}
    >
      <div className="flex flex-col gap-2 mt-4">
        <label htmlFor="shortTitle" className="font-semibold text-gray-700">
          {t(
            "Pages.Admin.AnalyticsPages.Create.ShortTitleLabel",
            "Page short title",
          )}{" "}
          <span className="text-red-500">*</span>
        </label>
        <TextInput
          inputId="shortTitle"
          value={shortTitle}
          onChange={setShortTitle}
          maxLength={50}
          placeholder={t(
            "Pages.Admin.AnalyticsPages.Create.ShortTitlePlaceholder",
            "e.g. Overview",
          )}
          className="border border-gray-300 rounded-md px-2 py-1 w-full"
          showValidationErrors={touched.shortTitle && !isShortTitleValid}
          validationResult={
            touched.shortTitle
              ? {
                  isValid: isShortTitleValid,
                  errors: isShortTitleValid
                    ? []
                    : [
                        {
                          errorType: "REQUIRED",
                          customMessage: t(
                            "Pages.Admin.AnalyticsPages.Create.ShortTitleValidation",
                            "Required, maximum 50 characters",
                          ),
                        },
                      ],
                }
              : null
          }
        />
        <div className="text-xs text-gray-500">
          {t(
            "Pages.Admin.AnalyticsPages.Create.ShortTitleHelp",
            "This will show in the left navigation.",
          )}
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <label htmlFor="longTitle" className="font-semibold text-gray-700">
          {t(
            "Pages.Admin.AnalyticsPages.Create.LongTitleLabel",
            "Page long title",
          )}{" "}
          <span className="text-red-500">*</span>
        </label>
        <TextInput
          inputId="longTitle"
          value={longTitle}
          onChange={setLongTitle}
          maxLength={100}
          placeholder={t(
            "Pages.Admin.AnalyticsPages.Create.LongTitlePlaceholder",
            "e.g. Analytics overview for Q3",
          )}
          className="border border-gray-300 rounded-md px-2 py-1 w-full"
          showValidationErrors={touched.longTitle && !isLongTitleValid}
          validationResult={
            touched.longTitle
              ? {
                  isValid: isLongTitleValid,
                  errors: isLongTitleValid
                    ? []
                    : [
                        {
                          errorType: "REQUIRED",
                          customMessage: t(
                            "Pages.Admin.AnalyticsPages.Create.LongTitleValidation",
                            "Required, maximum 100 characters",
                          ),
                        },
                      ],
                }
              : null
          }
        />
        <div className="text-xs text-gray-500">
          {t(
            "Pages.Admin.AnalyticsPages.Create.LongTitleHelp",
            "This will show at the top of the page as the title.",
          )}
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <label
          htmlFor="sequenceBeforePageId"
          className="font-semibold text-gray-700"
        >
          {t("Pages.Admin.AnalyticsPages.Placement", "Placement")}{" "}
          <span className="text-red-500">*</span>
        </label>
        <select
          id="sequenceBeforePageId"
          value={sequenceBeforePageId ?? "end"}
          onChange={(e) => {
            const val = e.target.value;
            setSequenceBeforePageId(val === "end" ? null : parseInt(val));
          }}
          className="block w-full rounded-md border-gray-300 focus:border-primary focus:ring-primary"
        >
          {analyticsPages.map((page) => (
            <option key={page.pageId} value={page.pageId}>
              {t("Pages.Admin.AnalyticsPages.BeforePage", "Before {{title}}", {
                title: page.pageTitle,
              })}
            </option>
          ))}
          <option value="end">
            {t("Pages.Admin.AnalyticsPages.AtEnd", "At end")}
          </option>
        </select>
        <div className="text-xs text-gray-500">
          {t(
            "Pages.Admin.AnalyticsPages.Create.PlacementHelp",
            "Choose where to place the new page in the navigation order.",
          )}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <label
          htmlFor="journeyFilterRestriction"
          className="font-semibold text-gray-700"
        >
          {t(
            "Pages.Admin.AnalyticsPages.Create.JourneyFilterLabel",
            "Journey filter restriction",
          )}{" "}
          <span className="text-red-500">*</span>
        </label>
        <select
          id="journeyFilterRestriction"
          value={journeyFilterRestriction}
          onChange={(e) =>
            setJourneyFilterRestriction(
              e.target.value as AnalyticsPageJourneyFilterRestriction
            )
          }
          className="block w-full rounded-md border-gray-300 focus:border-primary focus:ring-primary"
        >
          <option value="SHOW-ALL">
            {t("Pages.Admin.AnalyticsPages.JourneyFilters.ShowAll", "Show all")}
          </option>
          <option value="INDUCTIONS-ONLY">
            {t(
              "Pages.Admin.AnalyticsPages.JourneyFilters.Inductions",
              "Inductions only",
            )}
          </option>
          <option value="EXITS-ONLY">
            {t(
              "Pages.Admin.AnalyticsPages.JourneyFilters.Exits",
              "Exits only",
            )}
          </option>
          <option value="SYSTEM-JOURNEYS-ONLY">
            {t(
              "Pages.Admin.AnalyticsPages.JourneyFilters.System",
              "System journeys only",
            )}
          </option>
          <option value="CLIENT-SENT-JOURNEYS-ONLY">
            {t(
              "Pages.Admin.AnalyticsPages.JourneyFilters.ClientSent",
              "Client-sent journeys only",
            )}
          </option>
        </select>
        <div className="text-xs text-gray-500">
          {t(
            "Pages.Admin.AnalyticsPages.Create.JourneyFilterHelp",
            "Restrict which journeys are shown for this analytics page.",
          )}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <label
          htmlFor="defaultJourneyFilterPattern"
          className="font-semibold text-gray-700"
        >
          {t(
            "Pages.Admin.AnalyticsPages.DefaultJourneyPattern",
            "Default journey filter pattern",
          )}
        </label>
        <TextInput
          inputId="defaultJourneyFilterPattern"
          value={defaultJourneyFilterPattern}
          onChange={setDefaultJourneyFilterPattern}
          maxLength={100}
          placeholder={t(
            "Pages.Admin.AnalyticsPages.Create.DefaultPatternPlaceholder",
            "e.g. End Year Review*",
          )}
          className="border border-gray-300 rounded-md px-2 py-1 w-full"
          showValidationErrors={
            touched.defaultJourneyFilterPattern &&
            !isDefaultJourneyFilterPatternValid
          }
          validationResult={
            touched.defaultJourneyFilterPattern
              ? {
                  isValid: isDefaultJourneyFilterPatternValid,
                  errors: isDefaultJourneyFilterPatternValid
                    ? []
                    : [
                        {
                          errorType: "REQUIRED",
                          customMessage: t(
                            "Pages.Admin.AnalyticsPages.Create.DefaultPatternValidation",
                            "Maximum 100 characters",
                          ),
                        },
                      ],
                }
              : null
          }
        />
        <div className="text-xs text-gray-500">
          {t(
            "Pages.Admin.AnalyticsPages.Create.DefaultPatternHelp",
            'Usually blank. Enter a pattern such as End Year Review* to select the most recent journey beginning with "End Year Review".',
          )}
        </div>
      </div>
      <div className="rounded border border-amber-200 bg-amber-50 p-2 text-sm text-amber-800">
        {t(
          "Pages.Admin.AnalyticsPages.Create.WidgetRequiredNotice",
          "After adding a page, add a visible widget before the page will show in the left navigation.",
        )}
      </div>
      <div className="flex gap-3 justify-end mt-2">
        <button
          type="button"
          className="btn-secondary"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          {t("Common.Cancel", "Cancel")}
        </button>
        <button
          type="submit"
          className="btn-primary"
          disabled={!isFormValid || isSubmitting}
        >
          {isSubmitting
            ? t("Common.Saving", "Saving")
            : t("Pages.Admin.AnalyticsPages.AddPage", "Add page")}
        </button>
      </div>
    </form>
  );
};

export default CreateAnalyticsPage;
